/**
 * @file Offline-tolerant queue for bidder pick / loaded-out writes.
 *
 * Pickup days run on yard wifi that drops out. A queued write is stored in
 * localStorage and replayed until the server confirms it, so staff can keep
 * marking bidders through a dropout. Replay is safe because the toggle
 * endpoints accept an explicit desired value rather than flipping.
 */

import { togglePickedStatus, toggleMarkedPickedStatus } from '../api/bidders';

export const PICK_QUEUE_STORAGE_KEY = 'equipbid.pendingPicks';
export const PICK_QUEUE_EVENT = 'pick-queue-changed';

/** Column names, matching the bidder row fields the table renders. */
export const PICK_FIELD_LOADED_OUT = 'is_picked';
export const PICK_FIELD_MARKED_PICKED = 'is_marked_picked';

/** Abandon writes this old rather than replaying a stale pick days later. */
const MAX_ENTRY_AGE_MS = 24 * 60 * 60 * 1000;

let syncing = false;
let lastError = null;
let flushPromise = null;
let rerunRequested = false;

const entryKey = (auctionGroupId, bidderId, field) =>
  `${auctionGroupId ?? ''}:${bidderId}:${field}`;

function notify() {
  window.dispatchEvent(new CustomEvent(PICK_QUEUE_EVENT));
}

/**
 * @returns {Array<{key: string, bidderId: string, auctionGroupId: number|null,
 *   field: string, value: boolean, queuedAt: number}>}
 */
export function readPickQueue() {
  let parsed;
  try {
    parsed = JSON.parse(localStorage.getItem(PICK_QUEUE_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const cutoff = Date.now() - MAX_ENTRY_AGE_MS;
  return parsed.filter(
    (entry) =>
      entry &&
      typeof entry.key === 'string' &&
      typeof entry.value === 'boolean' &&
      (entry.field === PICK_FIELD_LOADED_OUT || entry.field === PICK_FIELD_MARKED_PICKED) &&
      Number(entry.queuedAt) > cutoff
  );
}

function writePickQueue(entries) {
  try {
    localStorage.setItem(PICK_QUEUE_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or blocked — the in-memory optimistic update still stands.
  }
  notify();
  return entries;
}

/**
 * Record a pending write, replacing any earlier one for the same bidder+field
 * so the queue always holds the user's latest intent.
 * @param {{bidderId: string|number, auctionGroupId: number|null, field: string, value: boolean}} change
 */
export function enqueuePick({ bidderId, auctionGroupId, field, value }) {
  const key = entryKey(auctionGroupId, bidderId, field);
  const entries = readPickQueue().filter((entry) => entry.key !== key);
  entries.push({
    key,
    bidderId: String(bidderId),
    auctionGroupId: auctionGroupId ?? null,
    field,
    value,
    queuedAt: Date.now(),
  });
  return writePickQueue(entries);
}

/** Drop a pending write once the server has confirmed (or permanently rejected) it. */
export function dequeuePick(key) {
  return writePickQueue(readPickQueue().filter((entry) => entry.key !== key));
}

export function clearPickQueue() {
  return writePickQueue([]);
}

/** Dismiss a reported sync failure once the user has acted on it. */
export function clearPickQueueError() {
  lastError = null;
  notify();
}

export function getPickQueueStatus() {
  const pending = readPickQueue();
  return { pending, pendingCount: pending.length, syncing, lastError };
}

/**
 * Overlay unsynced values onto server rows so a refetch that has not caught up
 * yet cannot visually revert a pick the user already made.
 * @param {object[]} bidders
 * @param {number|null} auctionGroupId
 * @param {object[]} [pending] Queue entries; read from storage when omitted.
 * @returns {object[]}
 */
export function applyPendingPicks(bidders, auctionGroupId, pending = readPickQueue()) {
  if (!Array.isArray(bidders) || bidders.length === 0) return bidders;
  if (pending.length === 0) return bidders;

  const overrides = new Map();
  for (const entry of pending) {
    if (auctionGroupId != null && entry.auctionGroupId !== auctionGroupId) continue;
    const existing = overrides.get(entry.bidderId) || {};
    existing[entry.field] = entry.value;
    overrides.set(entry.bidderId, existing);
  }
  if (overrides.size === 0) return bidders;

  return bidders.map((bidder) => {
    const override = overrides.get(String(bidder.bidder_id));
    return override ? { ...bidder, ...override } : bidder;
  });
}

function sendEntry(entry) {
  return entry.field === PICK_FIELD_LOADED_OUT
    ? togglePickedStatus(entry.bidderId, entry.auctionGroupId, entry.value)
    : toggleMarkedPickedStatus(entry.bidderId, entry.auctionGroupId, entry.value);
}

/**
 * Replay every pending write. Network failures leave the entry queued for the
 * next attempt; a rejection from the server (gone, forbidden) drops it, since
 * retrying would never succeed.
 * @param {{onSynced?: (entry: object, result: object) => void,
 *   onRejected?: (entry: object, error: Error) => void}} [handlers]
 * @returns {Promise<{synced: number, remaining: number, error: string|null}>}
 */
export function flushPickQueue(handlers = {}) {
  const { onSynced, onRejected } = handlers;
  // A click during an in-flight pass is not covered by that pass's snapshot,
  // so ask for another round rather than leaving it to the retry timer.
  if (flushPromise) {
    rerunRequested = true;
    return flushPromise;
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.resolve({
      synced: 0,
      remaining: readPickQueue().length,
      error: null,
    });
  }

  flushPromise = (async () => {
    const entries = readPickQueue();
    if (entries.length === 0) {
      lastError = null;
      return { synced: 0, remaining: 0, error: null };
    }

    syncing = true;
    lastError = null;
    notify();

    let synced = 0;
    let error = null;

    for (const entry of entries) {
      try {
        const result = await sendEntry(entry);
        dequeuePick(entry.key);
        synced += 1;
        if (onSynced) onSynced(entry, result);
      } catch (err) {
        if (err?.isNetworkError) {
          error = err.message || 'Could not reach the server.';
          break;
        }
        // Permanent rejection — keeping it would block every later write.
        dequeuePick(entry.key);
        error = err?.message || 'The server rejected a pending change.';
        if (onRejected) onRejected(entry, err);
      }
    }

    syncing = false;
    lastError = error;
    notify();
    return { synced, remaining: readPickQueue().length, error };
  })();

  const settled = flushPromise.finally(() => {
    flushPromise = null;
    const rerun = rerunRequested;
    rerunRequested = false;
    // Terminates: a clean pass drains everything it saw, so anything left is new.
    if (rerun && !lastError && readPickQueue().length > 0) {
      flushPickQueue(handlers);
    }
  });

  return settled;
}
